import React from 'react';
import mediaQuery from 'css-mediaquery';
import { useMediaQuery } from '../../hooks';
import { BreakpointContext, BreakpointContextValues } from './BreakpointContext';
import { FixMeLater } from '../../utils';
import { MediaQueryKey, useHoneycombTheme } from '../../theming';

export type BreakpointProviderProps = {
    /**
     * The child components.
     */
    children: React.ReactNode;

    /**
     * A predicted viewport width that will be used to determine the correct breakpoint for a server side render.
     */
    ssrPredictedWidth?: number;
};

export function BreakpointProvider(props: BreakpointProviderProps): React.JSX.Element {
    const { children, ssrPredictedWidth } = props;

    // Prepare a server side match media override using the predicted width.
    const ssrMatchMedia = ssrPredictedWidth
        ? (query: string): FixMeLater => ({
              matches: mediaQuery.match(query, {
                  width: `${ssrPredictedWidth}px`,
              }),
          })
        : undefined;

    const { theme } = useHoneycombTheme();
    const { breakpoints } = theme;
    const { mediaQueries } = breakpoints;

    const extraExtraLargeUp = useMediaQuery(mediaQueries.extraExtraLargeUp, { ssrMatchMedia });
    const extraLargeOnly = useMediaQuery(mediaQueries.extraLargeOnly, { ssrMatchMedia });
    const extraLargeUp = useMediaQuery(mediaQueries.extraLargeUp, { ssrMatchMedia });
    const extraSmallOnly = useMediaQuery(mediaQueries.extraSmallOnly, { ssrMatchMedia });
    const extraSmallUp = useMediaQuery(mediaQueries.extraSmallUp, { ssrMatchMedia });
    const largeOnly = useMediaQuery(mediaQueries.largeOnly, { ssrMatchMedia });
    const largeUp = useMediaQuery(mediaQueries.largeUp, { ssrMatchMedia });
    const mediumOnly = useMediaQuery(mediaQueries.mediumOnly, { ssrMatchMedia });
    const mediumUp = useMediaQuery(mediaQueries.mediumUp, { ssrMatchMedia });
    const smallOnly = useMediaQuery(mediaQueries.smallOnly, { ssrMatchMedia });
    const smallUp = useMediaQuery(mediaQueries.smallUp, { ssrMatchMedia });

    const context: BreakpointContextValues = React.useMemo(() => {
        let values = {
            extraExtraLargeUp,
            extraLargeOnly,
            extraLargeUp,
            extraSmallOnly,
            extraSmallUp,
            largeOnly,
            largeUp,
            mediumOnly,
            mediumUp,
            smallOnly,
            smallUp,
        };
        // If none of our queries match then force "extraSmallOnly" to be true.
        const match = Object.keys(values).find((key) => values[key as MediaQueryKey] === true);
        if (!match) {
            values = { ...values, ...{ extraSmallOnly: true } };
        }
        return values;
    }, [
        extraExtraLargeUp,
        extraLargeOnly,
        extraLargeUp,
        extraSmallOnly,
        extraSmallUp,
        largeOnly,
        largeUp,
        mediumOnly,
        mediumUp,
        smallOnly,
        smallUp,
    ]);

    return <BreakpointContext.Provider value={context}>{children}</BreakpointContext.Provider>;
}
